/**
 * Camera controller: orbit, zoom, target tracking.
 */

import * as THREE from 'three';
import type { Vec3 } from '../types';
import { MEGAPLANET_THRUST_RADIUS } from '../constants';

export interface CameraState {
  target: THREE.Vector3;
  theta: number;      // horizontal rotation
  phi: number;        // vertical rotation (0 = top, PI = bottom)
  distance: number;
  targetDistance: number;  // for smooth zoom
  isDragging: boolean;
  lastMouse: { x: number; y: number };
  missileCam: boolean;
  manualPivot: boolean;   // pinned by the player (right-click / Tab); cleared by C, Esc and turn change
  /** When set, `target` glides toward it each frame (frame-rate independent) and clears on arrival. */
  glideTarget: THREE.Vector3 | null;
  /** Two-subject framing (the connecting shot cam): keep `a` and `b` both in frame, viewed side-on. */
  frame: CameraFrame | null;
  // Megaplanet surface camera blend
  surfaceBlend: number;         // 0=orbital, 1=surface (computed each frame)
  surfacePlanetRadius: number;  // radius of the planet player is on (0 if not megaplanet)
  surfaceNormal: THREE.Vector3; // player's surface normal (up direction for surface cam)
}

export function createCameraState(): CameraState {
  return {
    target: new THREE.Vector3(0, 0, 0),
    theta: 0,
    phi: Math.PI / 3,
    distance: 65,
    targetDistance: 65,
    isDragging: false,
    lastMouse: { x: 0, y: 0 },
    missileCam: false,
    manualPivot: false,
    glideTarget: null,
    frame: null,
    surfaceBlend: 0,
    surfacePlanetRadius: 0,
    surfaceNormal: new THREE.Vector3(0, 1, 0),
  };
}

export interface CameraFrame {
  a: THREE.Vector3;
  b: THREE.Vector3;
  /** Bodies that must not sit between the camera and either subject. */
  occluders: Array<{ x: number; y: number; z: number; r: number }>;
}

/** Framing limits: how close the two-shot may pull in, and how far it may back off before it stops fitting. */
export const FRAME_MIN_DISTANCE = 14;
export const FRAME_MAX_DISTANCE = 130;
/** Fraction of the view the two subjects may span (the rest is margin). */
const FRAME_FILL = 0.72;

/** Half-extent the view can hold at distance 1 (uses the tighter of the two axes). */
function frameHalfTan(camera: THREE.PerspectiveCamera): number {
  const vTan = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  const aspect = camera.aspect || 1.6;
  return vTan * Math.min(aspect, 1) * FRAME_FILL;
}

/** Distance at which two points `sep` apart fit the view side-on. */
export function frameDistanceFor(camera: THREE.PerspectiveCamera, sep: number): number {
  return (sep / 2) / frameHalfTan(camera) + 6;
}

/** True when a two-shot of these points is close enough to read (else: follow the projectile instead). */
export function frameWouldFit(camera: THREE.PerspectiveCamera, a: Vec3, b: Vec3): boolean {
  const sep = Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
  return frameDistanceFor(camera, sep) <= FRAME_MAX_DISTANCE;
}

/** Does the segment from `from` to `to` pass through a body (excluding its very end, which may rest on a surface)? */
function segmentBlocked(from: THREE.Vector3, to: THREE.Vector3, occ: CameraFrame['occluders']): boolean {
  const d = to.clone().sub(from);
  const len2 = d.lengthSq();
  if (len2 < 1e-6) return false;
  for (const o of occ) {
    const cx = o.x - from.x, cy = o.y - from.y, cz = o.z - from.z;
    let t = (cx * d.x + cy * d.y + cz * d.z) / len2;
    if (t <= 0.02 || t >= 0.97) continue;
    const px = from.x + d.x * t - o.x, py = from.y + d.y * t - o.y, pz = from.z + d.z * t - o.z;
    const r = o.r * 0.97;
    if (px * px + py * py + pz * pz < r * r) return true;
  }
  return false;
}

/** Hermite smoothstep: 0 at edge0, 1 at edge1 */
function smoothstep(x: number, edge0: number, edge1: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

export interface CameraControlOptions {
  /** A finger held still on the canvas for LONG_PRESS_MS — the touch stand-in for right-click. */
  onLongPress?: (clientX: number, clientY: number) => void;
}

export const LONG_PRESS_MS = 450;
const LONG_PRESS_SLOP_PX = 12;

export function setupCameraControls(
  canvas: HTMLCanvasElement,
  state: CameraState,
  options: CameraControlOptions = {},
): () => void {
  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) return; // only left-click drags
    state.isDragging = true;
    state.lastMouse = { x: e.clientX, y: e.clientY };
  };

  const onContextMenu = (e: MouseEvent) => {
    e.preventDefault(); // suppress right-click menu on canvas
  };

  const onMouseUp = () => {
    state.isDragging = false;
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!state.isDragging) return;
    state.theta -= (e.clientX - state.lastMouse.x) * 0.005;
    state.phi = Math.max(
      0.1,
      Math.min(Math.PI - 0.1, state.phi - (e.clientY - state.lastMouse.y) * 0.005)
    );
    state.lastMouse = { x: e.clientX, y: e.clientY };
  };

  const onWheel = (e: WheelEvent) => {
    let minDist = state.missileCam ? 3 : 12;
    let maxDist = 150;
    // On megaplanets, allow zooming much closer and further
    if (state.surfacePlanetRadius >= MEGAPLANET_THRUST_RADIUS) {
      minDist = state.surfacePlanetRadius + 2;
      maxDist = state.surfacePlanetRadius * 3;
    }
    state.targetDistance = Math.max(minDist, Math.min(maxDist, state.targetDistance + e.deltaY * 0.05));
    e.preventDefault();
  };

  // Touch: one finger orbits, two fingers pinch-zoom, a still finger long-presses
  let pinchDist = 0;
  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  let touchStart = { x: 0, y: 0 };
  const cancelLongPress = () => { if (longPressTimer !== null) { clearTimeout(longPressTimer); longPressTimer = null; } };
  const zoomBy = (delta: number) => {
    let minDist = state.missileCam ? 3 : 12;
    let maxDist = 150;
    if (state.surfacePlanetRadius >= MEGAPLANET_THRUST_RADIUS) {
      minDist = state.surfacePlanetRadius + 2;
      maxDist = state.surfacePlanetRadius * 3;
    }
    state.targetDistance = Math.max(minDist, Math.min(maxDist, state.targetDistance + delta));
  };

  const onTouchStart = (e: TouchEvent) => {
    cancelLongPress();
    if (e.touches.length === 1) {
      state.isDragging = true;
      touchStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      state.lastMouse = { ...touchStart };
      if (options.onLongPress) {
        longPressTimer = setTimeout(() => {
          longPressTimer = null;
          state.isDragging = false;
          options.onLongPress!(touchStart.x, touchStart.y);
        }, LONG_PRESS_MS);
      }
    } else if (e.touches.length === 2) {
      state.isDragging = false;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchDist = Math.hypot(dx, dy);
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const d = Math.hypot(dx, dy);
      if (pinchDist > 0) zoomBy((pinchDist - d) * 0.15);
      pinchDist = d;
      cancelLongPress();
      e.preventDefault();
      return;
    }
    if (!state.isDragging || e.touches.length !== 1) return;
    const x = e.touches[0].clientX, y = e.touches[0].clientY;
    if (longPressTimer !== null && Math.hypot(x - touchStart.x, y - touchStart.y) > LONG_PRESS_SLOP_PX) cancelLongPress();
    const dx = x - state.lastMouse.x;
    const dy = y - state.lastMouse.y;
    state.theta -= dx * 0.005;
    state.phi = Math.max(0.1, Math.min(Math.PI - 0.1, state.phi - dy * 0.005));
    state.lastMouse = { x, y };
    e.preventDefault();
  };

  const onTouchEnd = (e: TouchEvent) => {
    cancelLongPress();
    if (e.touches.length < 2) pinchDist = 0;
    if (e.touches.length === 0) state.isDragging = false;
  };

  canvas.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('mousemove', onMouseMove);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('touchstart', onTouchStart);
  canvas.addEventListener('touchmove', onTouchMove, { passive: false });
  canvas.addEventListener('touchend', onTouchEnd);

  // Return cleanup function
  return () => {
    canvas.removeEventListener('mousedown', onMouseDown);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('mousemove', onMouseMove);
    canvas.removeEventListener('wheel', onWheel);
    canvas.removeEventListener('contextmenu', onContextMenu);
    canvas.removeEventListener('touchstart', onTouchStart);
    canvas.removeEventListener('touchmove', onTouchMove);
    canvas.removeEventListener('touchend', onTouchEnd);
    cancelLongPress();
  };
}

/** Per-frame lerp factor `t` (tuned at 60 fps) converted to this frame's dt. */
export function frameLerp(t: number, dt: number): number {
  return 1 - Math.pow(1 - t, dt * 60);
}

export function updateCamera(
  camera: THREE.PerspectiveCamera,
  state: CameraState,
  dt = 1 / 60
): void {
  // Smooth zoom (frame-rate independent)
  state.distance += (state.targetDistance - state.distance) * frameLerp(0.1, dt);

  // Two-subject framing: midpoint target, side-on angle, distance that fits both
  if (state.frame) applyFrame(camera, state, dt);

  // Glide the orbit target toward a requested focus
  if (state.glideTarget) {
    state.target.lerp(state.glideTarget, frameLerp(0.12, dt));
    if (state.target.distanceToSquared(state.glideTarget) < 0.0025) {
      state.target.copy(state.glideTarget);
      state.glideTarget = null;
    }
  }

  // Orbital camera position (standard)
  const x = state.distance * Math.sin(state.phi) * Math.cos(state.theta);
  const y = state.distance * Math.cos(state.phi);
  const z = state.distance * Math.sin(state.phi) * Math.sin(state.theta);

  const orbitalPos = new THREE.Vector3(
    state.target.x + x,
    state.target.y + y,
    state.target.z + z
  );

  // Compute surface blend for megaplanets
  const r = state.surfacePlanetRadius;
  if (r >= MEGAPLANET_THRUST_RADIUS) {
    const surfaceHeight = state.distance - r;
    // Blend: 1 (surface) when close, 0 (orbital) when far
    state.surfaceBlend = 1 - smoothstep(surfaceHeight, r * 0.3, r * 0.8);
  } else {
    state.surfaceBlend = 0;
  }

  if (state.surfaceBlend > 0.001) {
    // Surface camera: hover above terrain, look along surface tangent
    const normal = state.surfaceNormal;
    const surfaceHeight = Math.max(2, state.distance - r);

    // Position: above player along surface normal
    const surfacePos = new THREE.Vector3(
      state.target.x + normal.x * surfaceHeight,
      state.target.y + normal.y * surfaceHeight,
      state.target.z + normal.z * surfaceHeight,
    );

    // Surface lookAt: along the surface (perpendicular to normal, in theta direction)
    const tangent = new THREE.Vector3(
      -Math.sin(state.theta),
      0,
      Math.cos(state.theta),
    );
    // Project tangent onto surface plane (remove normal component)
    const dot = tangent.dot(normal);
    tangent.sub(normal.clone().multiplyScalar(dot)).normalize();

    const surfaceLookAt = state.target.clone().add(tangent.multiplyScalar(5));

    // Interpolate between orbital and surface
    const blend = state.surfaceBlend;
    camera.position.lerpVectors(orbitalPos, surfacePos, blend);

    const lerpTarget = new THREE.Vector3().lerpVectors(state.target, surfaceLookAt, blend);
    camera.lookAt(lerpTarget);

    // Interpolate up vector: orbital uses default (0,1,0), surface uses normal
    const orbitalUp = new THREE.Vector3(0, 1, 0);
    const surfaceUp = normal.clone();
    camera.up.lerpVectors(orbitalUp, surfaceUp, blend).normalize();
  } else {
    camera.up.set(0, 1, 0); // reset after a surface-cam blend
    camera.position.copy(orbitalPos);
    camera.lookAt(state.target);
  }
}

const _mid = new THREE.Vector3();
const _u = new THREE.Vector3();
const _cur = new THREE.Vector3();
const _cand = new THREE.Vector3();
const _camPos = new THREE.Vector3();
const FRAME_ROTATIONS = [0, Math.PI / 3, -Math.PI / 3, (2 * Math.PI) / 3, -(2 * Math.PI) / 3, Math.PI];

function applyFrame(camera: THREE.PerspectiveCamera, state: CameraState, dt: number): void {
  const f = state.frame!;
  state.glideTarget = null;
  _mid.copy(f.a).add(f.b).multiplyScalar(0.5);
  state.target.lerp(_mid, frameLerp(0.15, dt));

  const sep = f.a.distanceTo(f.b);
  const want = Math.max(FRAME_MIN_DISTANCE, Math.min(FRAME_MAX_DISTANCE, frameDistanceFor(camera, sep)));
  state.targetDistance += (want - state.targetDistance) * frameLerp(0.1, dt);

  if (state.isDragging || sep < 0.5) return; // the player is looking around, or the subjects have met

  // View direction perpendicular to the a→b line, as close to the current one as possible,
  // swung around that line until neither subject is hidden behind a body
  _u.copy(f.b).sub(f.a).normalize();
  _cur.set(Math.sin(state.phi) * Math.cos(state.theta), Math.cos(state.phi), Math.sin(state.phi) * Math.sin(state.theta));
  const base = _cur.clone().addScaledVector(_u, -_cur.dot(_u));
  if (base.lengthSq() < 1e-6) base.crossVectors(_u, Math.abs(_u.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0));
  base.normalize();
  let chosen: THREE.Vector3 | null = null;
  for (const rot of FRAME_ROTATIONS) {
    _cand.copy(base).applyAxisAngle(_u, rot);
    _camPos.copy(_mid).addScaledVector(_cand, want);
    if (!segmentBlocked(_camPos, f.a, f.occluders) && !segmentBlocked(_camPos, f.b, f.occluders)) { chosen = _cand.clone(); break; }
  }
  const v = chosen ?? base;
  const targetPhi = Math.max(0.1, Math.min(Math.PI - 0.1, Math.acos(Math.max(-1, Math.min(1, v.y)))));
  const targetTheta = Math.atan2(v.z, v.x);
  let dTheta = targetTheta - state.theta;
  while (dTheta > Math.PI) dTheta -= 2 * Math.PI;
  while (dTheta < -Math.PI) dTheta += 2 * Math.PI;
  const k = frameLerp(0.08, dt);
  state.theta += dTheta * k;
  state.phi += (targetPhi - state.phi) * k;
}

/** Frame two points together (the connecting shot cam). Call every frame while it should hold. */
export function setCameraFrame(state: CameraState, a: Vec3, b: Vec3, occluders: CameraFrame['occluders']): void {
  if (!state.frame) state.frame = { a: new THREE.Vector3(), b: new THREE.Vector3(), occluders };
  state.frame.a.set(a.x, a.y, a.z);
  state.frame.b.set(b.x, b.y, b.z);
  state.frame.occluders = occluders;
}

export function clearCameraFrame(state: CameraState): void {
  state.frame = null;
}

/** Snap the orbit target (cancels any glide). */
export function setCameraTarget(state: CameraState, target: Vec3): void {
  state.glideTarget = null;
  state.target.set(target.x, target.y, target.z);
}

/** Glide the orbit target to a new focus over a few frames, optionally with a zoom. */
export function glideCameraTo(state: CameraState, target: Vec3, zoom?: number): void {
  state.glideTarget = new THREE.Vector3(target.x, target.y, target.z);
  if (zoom !== undefined) setCameraZoom(state, zoom);
}

const _lerpTmp = new THREE.Vector3();
/** Smoothly move the orbit target. `t` is the per-frame factor at 60 fps; pass `dt` to make it frame-rate independent. */
export function lerpCameraTarget(state: CameraState, target: Vec3, t: number, dt?: number): void {
  state.glideTarget = null; // a follow overrides a pending glide
  state.target.lerp(_lerpTmp.set(target.x, target.y, target.z), dt === undefined ? t : frameLerp(t, dt));
}

export function setCameraZoom(state: CameraState, distance: number): void {
  const minDist = state.missileCam ? 3 : 12;
  state.targetDistance = Math.max(minDist, Math.min(500, distance));
}

export function toggleMissileCam(state: CameraState): void {
  state.missileCam = !state.missileCam;
}

export function setMissileCam(state: CameraState, enabled: boolean): void {
  state.missileCam = enabled;
}

export function setManualPivot(state: CameraState, enabled: boolean): void {
  state.manualPivot = enabled;
}
